import React, { useEffect, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import AdvTabs from '../components/AdvTabs';
import { getLuminance } from '../utils/ColorLuminance.js';

const NOTICE_OPEN_STORAGE_KEY = 'lilydale-oasis-notice-open';
const NOTICE_DISMISSED_STORAGE_KEY = 'lilydale-oasis-dismissed-notice-ids';

const NOTICE_ITEMS = [
  {
    id: 'emergency-1',
    category: 'emergency',
    title: 'Emergency',
    message:
      'Traffic advisory: A multi-vehicle crash has been reported near Yarrawonga at the Murray Valley Highway and Burkes Drive intersection.',
  },
  {
    id: 'promotions-1',
    category: 'promotions',
    title: 'Promotions and Events',
    message:
      'Limited-time offer: Stay 2 nights and receive a complimentary breakfast upgrade.',
  },
];

const LilydaleOasisHome = ({ hotelInfo, quickLinks, onQuickLinkClick, theme }) => {
  const [isNoticeOpen, setIsNoticeOpen] = useState(() => {
    const savedState = sessionStorage.getItem(NOTICE_OPEN_STORAGE_KEY);
    return savedState === null ? true : savedState === 'true';
  });
  const [dismissedNoticeIds, setDismissedNoticeIds] = useState(() => {
    const savedIds = sessionStorage.getItem(NOTICE_DISMISSED_STORAGE_KEY);

    if (!savedIds) {
      return [];
    }

    try {
      const parsedIds = JSON.parse(savedIds);
      return Array.isArray(parsedIds) ? parsedIds : [];
    } catch {
      return [];
    }
  });
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselTransitionTime, setCarouselTransitionTime] = useState(500);
  const homeImageCount = hotelInfo?.landing?.homeImages?.length || 0;
  const carouselItemCount = homeImageCount > 0 ? homeImageCount + 1 : 0;

  const visibleNotices = NOTICE_ITEMS.filter((item) => !dismissedNoticeIds.includes(item.id));
  const currentNotice = visibleNotices[0] || null;
  const hasNotice = Boolean(currentNotice);

  useEffect(() => {
    if (carouselItemCount === 0) {
      setCarouselIndex(0);
      return;
    }
    if (carouselIndex >= carouselItemCount) {
      setCarouselIndex(0);
    }
  }, [carouselItemCount, carouselIndex]);

  useEffect(() => {
    if (carouselItemCount <= 1) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCarouselIndex((prev) => {
        if (prev >= carouselItemCount - 1) {
          setCarouselTransitionTime(0);
          return 0;
        }
        setCarouselTransitionTime(500);
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(timer);
  }, [carouselItemCount]);

  useEffect(() => {
    if (carouselTransitionTime !== 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setCarouselTransitionTime(500);
    }, 80);
    return () => clearTimeout(timer);
  }, [carouselTransitionTime]);

  useEffect(() => {
    sessionStorage.setItem(NOTICE_OPEN_STORAGE_KEY, String(isNoticeOpen));
  }, [isNoticeOpen]);

  useEffect(() => {
    sessionStorage.setItem(NOTICE_DISMISSED_STORAGE_KEY, JSON.stringify(dismissedNoticeIds));
  }, [dismissedNoticeIds]);

  if (!hotelInfo) { return <div>Loading...</div>; }

  const themeColor = theme?.primaryColor || theme?.color || 'rgb(0,0,0)';
  const textColor = theme?.secondaryColor || 'rgb(255,255,255)';
  const designedColor = getLuminance(themeColor) > 0.7 ? textColor : themeColor;

  const homeHeader = (theme?.color || theme?.secondaryColor) ? designedColor : (theme?.homeHeader || 'rgb(0,0,0)');
  const homeContent = (theme?.color || theme?.secondaryColor) ? designedColor : (theme?.homeContent || 'rgb(75,85,99)');
  const homeQuickLinksEntries = (theme?.color || theme?.secondaryColor) ? designedColor : (theme?.homeQuickLinksEntries || 'rgb(0,0,0)');
  const homeBackground = (theme?.color || theme?.secondaryColor) ? "rgb(255,255,255)" : (theme?.homeBackground || 'rgb(255,255,255)');
  const homeSquareImage = hotelInfo?.landing?.homeSquareImage || '';
  const noticeCardTone = currentNotice?.category === 'promotions'
    ? { borderColor: '#bfdbfe', titleColor: '#1e3a8a' }
    : { borderColor: '#f3c7c7', titleColor: '#7f1d1d' };

  const closeCurrentNotice = () => {
    if (!currentNotice) {
      setIsNoticeOpen(false);
      return;
    }

    setDismissedNoticeIds((prev) => [...prev, currentNotice.id]);

    if (visibleNotices.length <= 1) {
      setIsNoticeOpen(false);
    }
  };

  const handleCarouselChange = (index) => {
    setCarouselTransitionTime(500);
    setCarouselIndex(index);
  };

  return (
    <div style={{ backgroundColor: homeBackground }}>
      <div className='mb-4'>
        {hotelInfo && hotelInfo.landing && hotelInfo.landing.homeImages.length > 0 && (
          <div>
            <Carousel
              showArrows={true}
              showThumbs={false}
              infiniteLoop={false}
              autoPlay={false}
              selectedItem={carouselIndex}
              onChange={handleCarouselChange}
              transitionTime={carouselTransitionTime}
              showIndicators={false}
              showStatus={false}
              className="w-full h-full"
            >
              {hotelInfo.landing.homeImages.map((slide, index) => (
                <div key={`home-slide-${index}`} className="w-full h-full">
                  <img
                    src={slide}
                    alt={`Home ${index + 1}`}
                    className="object-cover w-full h-36"
                  />
                </div>
              ))}
              {homeSquareImage && (
                <div key="home-square-slot" className="w-full h-full">
                  <img src={homeSquareImage} alt="Yarrawonga promotions banner" className="h-36 w-full object-cover" />
                </div>
              )}
            </Carousel>
          </div>
        )}
        {hotelInfo && hotelInfo.landing && hotelInfo.landing.homeTitle.length > 0 &&
          <p className='text-xl text-center p-2 font-semibold' style={{ color: homeHeader }}>{hotelInfo.landing.homeTitle}</p>}
        {hotelInfo && hotelInfo.landing && hotelInfo.landing.homeDescription.length > 0 &&
          <p className='px-6 text-xs whitespace-pre-wrap' style={{ homeContent }}>{hotelInfo.landing.homeDescription}</p>}
      </div>
      <AdvTabs origin={'Home'} />
      {quickLinks.length > 0 && (
        <div className="text-center">
          <p className="text-xl font-semibold p-2" style={{ color: homeHeader }}>
            Quick Links
          </p>
          <div className="grid grid-cols-2 max-w-md mx-auto px-2">
            {quickLinks.map((data, index) => (
              <div key={index} className="flex flex-col items-center px-2 py-1"
                onClick={() => onQuickLinkClick(data)}>
                <div className="w-full aspect-w-1 aspect-h-1">
                  <img
                    src={data.image}
                    alt={`Quick Link ${index + 1}`}
                    className="object-cover rounded-lg w-full h-full"
                  />
                </div>
                <p className="mt-2 font-semibold" style={{ color: homeQuickLinksEntries }}>{data.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {isNoticeOpen && hasNotice && currentNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-3 backdrop-blur-[1px]">
          <div
            className="relative overflow-hidden rounded-md border border-[#d6dde8] bg-white shadow-2xl"
            style={{ width: 'clamp(320px, 56vw, 460px)' }}
          >
            <div className="flex items-end justify-between border-b border-[#d6dde8] bg-[#eef2f8] px-3 pt-2">
              <p className="pb-1 text-xs font-semibold uppercase tracking-wide text-[#334155]">Notice</p>
              <button
                type="button"
                className="mb-1 px-2 py-1 text-xs font-semibold text-[#475569] hover:bg-white"
                onClick={closeCurrentNotice}
              >
                x
              </button>
            </div>

            <div className="overflow-y-auto px-4 py-4" style={{ maxHeight: 'min(68vh, 520px)' }}>
              <div
                className="border px-3 py-1.5"
                style={{
                  borderColor: noticeCardTone.borderColor,
                }}
              >
                <p className="text-sm font-semibold leading-5" style={{ color: noticeCardTone.titleColor }}>
                  {currentNotice.title}
                </p>
              </div>

              <p className="mt-3 text-sm leading-7 text-[#334155]">{currentNotice.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LilydaleOasisHome;
